import { inject, Injectable, signal } from '@angular/core';
import { catchError, forkJoin, map, Observable, of, takeUntil, timeout } from 'rxjs';

import { SearchFilterField } from '../models/search-filter-spec.model';
import { AppStateResetService } from '../services/app-state-reset.service';
import { DropdownOption, dropdownListRows, DropdownService, LocationNode } from '../services/dropdown.service';
import { UserService } from '../services/user.service';
import { normalizeSearchText } from '../utils/search-normalize.util';
import { resolveDropdownRoleScope } from '../utils/dropdown-role-scope';
import { LICENSE_STATUS_OPTIONS } from '../../constants/license-status-options.constants';

/** Per-list request timeout; mirrors `FilterOptionsStore`'s `LIST_TIMEOUT_MS` (the dropdown
    endpoints have been observed to hang) — deliberately re-declared here rather than shared,
    since the two stores are independent by design (see class doc). */
const LIST_TIMEOUT_MS = 20000;

const STATUTORY_FLAGS = ['S', 'I'] as const;
const ROLE_FLAGS = ['P', 'R'] as const;

export interface VocabularyEntry {
  /** Normalized (case-folded, accent-stripped, hyphen-preserving) label — see
      `normalizeSearchText`. Every entry AND every typed query go through the same pass. */
  normalizedText: string;
  field: SearchFilterField;
  value: string | number;
  label: string;
  /** Present for entities/licenseType/department/user. */
  statutory?: 'S' | 'I';
  /** Present for department/user only. */
  role?: 'P' | 'R';
}

/** Outcome of one guarded list call: `rows` for indexing (always an array, even on failure)
    plus whether the fetch actually SUCCEEDED. `ok: false` marks a failed/timed-out call
    degraded to `[]` — the distinction a fetch-once latch must not lose, or a transient
    failure gets cached as "loaded" and is never retried (mirrors
    `FilterOptionsStore.ListResult`). */
interface ListOutcome<T> {
  ok: boolean;
  rows: T[];
}

/** One vocabulary CATEGORY's outcome (statuses / entities / licenseType / department / user):
    `entries` for indexing (concatenated regardless of `ok`, so a partial success — e.g. 3 of 4
    department S/I×role combos landing — still surfaces on screen) and `ok`, true only when
    EVERY sub-call the category makes succeeded. */
interface CategoryOutcome {
  ok: boolean;
  entries: VocabularyEntry[];
}

/** Maps flat `{id,name}` rows onto indexed vocabulary entries, dropping any row with no name
    (nothing to normalize/match against). */
function toEntries(
  rows: DropdownOption[],
  field: SearchFilterField,
  extra: Partial<Pick<VocabularyEntry, 'statutory' | 'role'>> = {},
): VocabularyEntry[] {
  return rows
    .filter((r) => r.name)
    .map((r) => ({
      normalizedText: normalizeSearchText(r.name),
      field,
      value: r.id,
      label: r.name,
      ...extra,
    }));
}

/** Flattens the location tree into flat `{id,name}` rows (depth-first, parent before
    children) so every level (Entity/Sub-Entity/Location) is independently searchable. */
function flattenLocations(nodes: LocationNode[]): DropdownOption[] {
  return nodes.flatMap((n) => [{ id: n.id, name: n.name }, ...flattenLocations(n.children ?? [])]);
}

/** Wraps ONE list call so a failure or timeout degrades to `{ok:false, rows:[]}` instead of
    tearing the assembling `forkJoin` down — the success/failure distinction {@link ListOutcome}
    exists to preserve. */
function safeListOutcome<T>(source: Observable<T[]>): Observable<ListOutcome<T>> {
  return source.pipe(
    timeout(LIST_TIMEOUT_MS),
    map((rows) => ({ ok: true, rows })),
    catchError(() => of({ ok: false, rows: [] as T[] })),
  );
}

/** Combines every sub-call outcome belonging to ONE vocabulary category into that category's
    single {@link CategoryOutcome}: `ok` is the AND of every sub-call (a category latches as
    "loaded" only when its ENTIRE fetch — every S/I × role combo it makes — succeeded), while
    `entries` concatenates every sub-call's rows regardless of `ok`, so a partial failure
    within one category still shows whatever DID load. */
function combineCategory(
  outcomes: Array<{ outcome: ListOutcome<DropdownOption>; field: SearchFilterField; tag: Partial<Pick<VocabularyEntry, 'statutory' | 'role'>> }>,
): CategoryOutcome {
  return {
    ok: outcomes.every((o) => o.outcome.ok),
    entries: outcomes.flatMap((o) => toEntries(o.outcome.rows, o.field, o.tag)),
  };
}

/**
 * Root-provided, fetch-once-per-identity vocabulary for intent-driven search (statuses,
 * locations, license types, departments, users — see the design spec's "Vocabulary loading"
 * section, `docs/superpowers/specs/2026-08-23-intent-driven-search-design.md`).
 *
 * Status is the one category that is NOT HTTP-backed: it is sourced synchronously from the
 * hardcoded `LICENSE_STATUS_OPTIONS` constant (`src/app/constants/license-status-options.constants.ts`),
 * the same wire-contract NAME list `WorkspacePageStore`/`LicensesReportsListStore` send as
 * `status_list`/`license_status` — deliberately NOT `DropdownService.statusList()`
 * (`/DropDown/get-status-list`), whose rows are `{id, name}` keyed by numeric database id.
 * Both target pages filter status by NAME, not id, and that endpoint's `name` is not even a
 * safe substitute for the wire value (`LICENSE_STATUS_OPTIONS`'s own doc comment: label and
 * wire value deliberately diverge for two entries). Matching a status term therefore yields
 * `value: LICENSE_STATUS_OPTIONS[i].value` (e.g. `'Expired'`), never a numeric id.
 *
 * Deliberately **separate** from `FilterOptionsStore`. `FilterOptionsStore`'s signals
 * (`userOptions`, `departmentOptions`, `locationTree`, `licenseTypeOptions`) are singletons
 * scoped to whatever the CURRENT page's own filter bar last requested. Driving them from a
 * background search-vocabulary fetch would silently overwrite what a live page's filter
 * dropdowns are showing (e.g. search prefetching Internal/Reviewer vocabulary while the user
 * is looking at a Statutory/Performer Workspace view would corrupt that page's filter bar
 * mid-session). This store calls the same underlying `DropdownService` methods directly, with
 * its own signals and its own cache, and never imports `FilterOptionsStore`.
 */
@Injectable({ providedIn: 'root' })
export class SearchVocabularyStore {
  private readonly dropdown = inject(DropdownService);
  private readonly user = inject(UserService);
  private readonly appReset = inject(AppStateResetService);

  private readonly _entries = signal<VocabularyEntry[]>([]);
  readonly entries = this._entries.asReadonly();

  /** Identity (`customer|user`) the cache holds a FULLY successful result for; `null` until
      one arrives. Latched only when EVERY vocabulary category's fetch succeeded (see
      `CategoryOutcome.ok` / the `subscribe` below) — never on "some rows arrived", so one
      failed/timed-out category (e.g. `locations()` hanging while the other categories succeed)
      can't get silently absorbed into a store-wide "loaded" latch and go permanently missing
      from search for the rest of the session. A partial failure still populates `_entries`
      with whatever DID load, but leaves `loadedFor` unset so the NEXT `ensureLoaded()` call
      retries everything (categories are not latched individually — see class doc for why a
      coarser, simpler retry-everything shape is an accepted trade-off here). Status is the one
      exception: it is a static constant (see `statuses$` below), never fails, and so never
      contributes to this partial-failure path. */
  private loadedFor: string | null = null;

  /** Identity whose fetch is currently IN FLIGHT; `null` when none is. Needed alongside
      `loadedFor` for the same reason as `FilterOptionsStore.independentInFlightFor`: a second
      `ensureLoaded()` call made before the first response arrives must not start a duplicate
      fetch. */
  private inFlightFor: string | null = null;

  constructor() {
    this.appReset.register(() => {
      this._entries.set([]);
      // Both markers, not just `loadedFor`: an in-flight request is cancelled by
      // `sessionEnd` (see below) BEFORE this callback runs, so its subscribe body — which
      // would otherwise clear `inFlightFor` — never executes. Leaving it set would make the
      // SAME user's next `ensureLoaded()` (same identity) a silent permanent no-op.
      this.loadedFor = null;
      this.inFlightFor = null;
    });
  }

  private identity(): string {
    return `${this.user.getCustomerId()}|${this.user.getUserId()}`;
  }

  /** Fetch once per caller identity (cached); a repeat call for the SAME identity is a no-op
      whether the first has already ARRIVED with a fully successful result (cached) or is
      still IN FLIGHT. A partial failure does NOT cache — the next call retries. */
  ensureLoaded(): void {
    const identity = this.identity();
    if (this.loadedFor === identity || this.inFlightFor === identity) return;
    this.inFlightFor = identity;

    const customer_id = this.user.getCustomerId();
    const user_id = this.user.getUserId();
    // An entity/license-type isn't P/R-scoped in meaning — resolved once against the
    // signed-in user's own system role (no page Role-filter selection to honor here).
    const resolvedRole = resolveDropdownRoleScope(undefined, this.user.getRole());

    // NOT fetched from `DropdownService.statusList()` — see the class doc above. Sourced
    // directly from the static `LICENSE_STATUS_OPTIONS` constant so the matched `value` is the
    // wire-contract NAME string Workspace/Reports actually filter by, never the numeric id
    // `/DropDown/get-status-list` returns. No HTTP call, so this "category" is synchronous and
    // always `ok: true` — it can never be the failing category in the partial-failure path
    // above, and contributes nothing to the 1/2/2/4/4-style HTTP call-count accounting.
    const statusEntries: VocabularyEntry[] = LICENSE_STATUS_OPTIONS.map((o) => ({
      normalizedText: normalizeSearchText(o.label),
      field: 'status' as const,
      value: o.value,
      label: o.label,
    }));
    const statuses$ = of<CategoryOutcome>({ ok: true, entries: statusEntries });

    // locations() returns a LocationsResponse body (a tree), not flat DropdownOption rows —
    // flatten before feeding safeListOutcome(). Its statutory key is `is_statutory_internal` —
    // NOT `is_statutory_or_internal`, unlike the other three endpoints below (confirmed
    // against `FilterOptionsStore.fetchLocations`).
    const locations$ = forkJoin(
      STATUTORY_FLAGS.map((statutory) =>
        safeListOutcome(
          this.dropdown
            .locations({ customer_id, user_id, role: resolvedRole, is_statutory_internal: statutory })
            .pipe(map((r) => flattenLocations(r.body?.data?.[0]?.location_list ?? []))),
        ).pipe(map((outcome) => ({ outcome, field: 'entities' as const, tag: { statutory } }))),
      ),
    ).pipe(map(combineCategory));

    const licenseTypes$ = forkJoin(
      STATUTORY_FLAGS.map((statutory) =>
        safeListOutcome(
          this.dropdown
            .licenseTypesByFlag({ customer_id, user_id, role: resolvedRole, is_statutory_or_internal: statutory })
            .pipe(map((r) => dropdownListRows(r.body))),
        ).pipe(map((outcome) => ({ outcome, field: 'licenseType' as const, tag: { statutory } }))),
      ),
    ).pipe(map(combineCategory));

    // Departments/users: BOTH role scopes must be independently checkable (design-spec scope
    // decision #2), so `role` is sent as the literal 'P'/'R' — not routed through
    // `resolveDropdownRoleScope` (which would map both onto the same 'EXCT' system role and
    // collapse the two into one fetch/tag).
    const departments$ = forkJoin(
      STATUTORY_FLAGS.flatMap((statutory) =>
        ROLE_FLAGS.map((role) =>
          safeListOutcome(
            this.dropdown
              .departmentList({ customer_id, user_id, role, is_statutory_or_internal: statutory })
              .pipe(map((r) => dropdownListRows(r.body))),
          ).pipe(map((outcome) => ({ outcome, field: 'department' as const, tag: { statutory, role } }))),
        ),
      ),
    ).pipe(map(combineCategory));

    const users$ = forkJoin(
      STATUTORY_FLAGS.flatMap((statutory) =>
        ROLE_FLAGS.map((role) =>
          safeListOutcome(
            this.dropdown
              .userList({ customer_id, user_id, role, is_statutory_or_internal: statutory })
              .pipe(map((r) => dropdownListRows(r.body))),
          ).pipe(map((outcome) => ({ outcome, field: 'user' as const, tag: { statutory, role } }))),
        ),
      ),
    ).pipe(map(combineCategory));

    forkJoin({
      statuses: statuses$,
      locations: locations$,
      licenseTypes: licenseTypes$,
      departments: departments$,
      users: users$,
    })
      .pipe(
        // Cancels the in-flight fetch on session teardown so a response for the PREVIOUS
        // user can never land after the reset callback above has cleared `_entries` and
        // re-fill it with stale data. Harmless on the outer pipe here (unlike a persistent
        // Subject-driven stream): this is a one-shot subscription per `ensureLoaded()` call,
        // so completing it changes nothing for a later call, which builds a fresh one.
        takeUntil(this.appReset.sessionEnd),
      )
      .subscribe((categories) => {
        this.inFlightFor = null;
        const entries: VocabularyEntry[] = [
          ...categories.statuses.entries,
          ...categories.locations.entries,
          ...categories.licenseTypes.entries,
          ...categories.departments.entries,
          ...categories.users.entries,
        ];
        this._entries.set(entries);
        // Latch only when EVERY category's fetch fully succeeded — see `loadedFor`'s doc.
        // A partial success still populates `entries` above (best-effort UX), but must not
        // latch, or the failed category's vocabulary is permanently absent from search with
        // no retry for the rest of the session.
        const allOk =
          categories.statuses.ok &&
          categories.locations.ok &&
          categories.licenseTypes.ok &&
          categories.departments.ok &&
          categories.users.ok;
        if (allOk) {
          this.loadedFor = identity;
        }
      });
  }
}
